<template>
  <div class="division-management-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#005f32"
      spinner="bar-fade-scale"
    />

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="showsnackbar" :color="snackbarColor" right :timeout="3500">
      <div class="d-flex align-center snackbar-content">
        <div class="snackbar-msg">{{ msg }}</div>
        <v-btn small :ripple="false" icon dark class="ml-2" @click="showsnackbar = false">
          <v-icon style="color: white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <v-container fluid class="pa-4">
      <!-- Top Action Bar -->
      <v-card class="elevation-1 rounded-xl mb-6 pa-4 bg-white">
        <v-layout wrap align-center justify-space-between>
          <v-flex xs12 sm6 md4 class="d-flex align-center py-2">
            <v-icon color="#005f32" class="mr-2" size="28">mdi-source-branch</v-icon>
            <div>
              <h2 class="kumbhBold mb-0 primary-text" style="font-size: 22px; color: #005f32;">
                Divisions & Projects
              </h2>
              <span class="text-caption grey--text text--darken-1">
                Manage organization divisions and assign projects
              </span>
            </div>
          </v-flex>

          <v-flex xs12 sm6 md8 class="toolbar-actions d-flex align-center justify-end flex-wrap py-2">
            <!-- Expand / Collapse All Toggle -->
            <v-btn
              outlined
              small
              color="#005f32"
              class="rounded-xl mr-3 text-none font-weight-medium"
              @click="toggleAllDivisions"
              v-if="divisionlist.length > 0"
            >
              <v-icon left small>
                {{ areAllCollapsed ? 'mdi-arrow-expand-vertical' : 'mdi-arrow-collapse-vertical' }}
              </v-icon>
              {{ areAllCollapsed ? 'Expand All' : 'Minimize All' }}
            </v-btn>

            <v-text-field
              class="rounded-xl mr-3 search-field"
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-magnify"
              placeholder="Search divisions or projects..."
              v-model="searchKeyword"
            ></v-text-field>

            <v-btn
              style="font-family: kumbhBold"
              dark
              class="rounded-xl px-4 text-none"
              color="#005f32"
              elevation="2"
              @click="dialog = true"
            >
              <v-icon left>mdi-plus</v-icon>
              Add Division
            </v-btn>
          </v-flex>
        </v-layout>
      </v-card>

      <!-- Division List (Full Horizontal Space) -->
      <div v-if="filteredDivisions.length > 0">
        <v-card
          v-for="item in filteredDivisions"
          :key="item._id"
          class="elevation-2 rounded-xl mb-5 division-card"
          outlined
        >
          <!-- Division Header (Clickable to Minimize / Expand) -->
          <div
            class="division-header px-5 py-4 d-flex align-center justify-space-between flex-nowrap"
            @click="toggleDivision(item._id)"
            role="button"
            :title="isCollapsed(item._id) ? 'Click to expand division' : 'Click to minimize division'"
          >
            <div class="division-info d-flex align-center flex-wrap" style="flex: 1;">
              <div class="division-title-badge mr-3 mb-1 d-flex align-center">
                <span class="kumbhBold division-name">{{ item.divisiontName }}</span>
              </div>

              <v-chip
                small
                outlined
                color="#005f32"
                class="mr-2 mb-1 font-weight-medium"
              >
                <v-icon left x-small>mdi-domain</v-icon>
                Dept: {{ item.departmentId ? item.departmentId.deptName : 'N/A' }}
              </v-chip>

              <v-chip
                small
                color="#e8f5e9"
                text-color="#005f32"
                class="font-weight-bold mb-1"
              >
                <v-icon left x-small color="#005f32">mdi-folder-outline</v-icon>
                {{ getProjectsForDivision(item._id).length }} Projects
              </v-chip>
            </div>

            <!-- Division Actions and Minimize Chevron -->
            <div class="division-actions d-flex align-center ml-2">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    icon
                    color="#005f32"
                    class="mr-1 action-btn"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="editDivision(item)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Edit Division</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    icon
                    color="error"
                    class="mr-2 action-btn"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="confirmDeleteDivision(item)"
                  >
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </template>
                <span>Delete Division</span>
              </v-tooltip>

              <!-- Chevron indicator -->
              <v-icon
                color="#005f32"
                class="collapse-chevron"
                :class="{ 'chevron-rotated': isCollapsed(item._id) }"
              >
                mdi-chevron-up
              </v-icon>
            </div>
          </div>

          <!-- Projects Section (Collapsible) -->
          <v-expand-transition>
            <div v-show="!isCollapsed(item._id)">
              <v-divider></v-divider>

              <div class="projects-section pa-4">
                <div class="d-flex align-center mb-3">
                  <span class="text-subtitle-2 grey--text text--darken-2 kumbhBold text-uppercase">
                    Projects under {{ item.divisiontName }}
                  </span>
                </div>

                <v-layout wrap>
                  <!-- List of Projects -->
                  <v-flex
                    v-for="project in getProjectsForDivision(item._id)"
                    :key="project._id"
                    xs12
                    sm6
                    md4
                    lg3
                    class="pa-2"
                  >
                    <v-card class="project-card rounded-lg pa-3 elevation-1" outlined>
                      <div class="d-flex justify-space-between align-start mb-2">
                        <div class="project-title kumbhBold" :title="project.projectName">
                          {{ project.projectName }}
                        </div>
                        <div class="d-flex align-center">
                          <v-btn
                            x-small
                            icon
                            color="#005f32"
                            title="Edit Project"
                            @click="openEditProject(project)"
                          >
                            <v-icon small>mdi-pencil</v-icon>
                          </v-btn>
                          <v-btn
                            x-small
                            icon
                            color="error"
                            title="Delete Project"
                            @click="confirmDeleteProject(project)"
                          >
                            <v-icon small>mdi-delete-outline</v-icon>
                          </v-btn>
                        </div>
                      </div>

                      <div v-if="project.location" class="project-location mb-1 text-caption grey--text text--darken-2 d-flex align-center">
                        <v-icon x-small color="#005f32" class="mr-1">mdi-map-marker-outline</v-icon>
                        <span class="text-truncate">{{ project.location }}</span>
                      </div>

                      <div v-if="project.description" class="project-desc text-caption grey--text text--darken-1 mb-2">
                        {{ project.description }}
                      </div>

                      <div v-if="project.department && project.department[0]" class="mt-2 pt-2 border-top-subtle d-flex align-center justify-space-between">
                        <span class="text-caption grey--text">Dept:</span>
                        <span class="text-caption font-weight-medium text-truncate ml-1" style="max-width: 140px;">
                          {{ project.department[0].deptName }}
                        </span>
                      </div>
                    </v-card>
                  </v-flex>

                  <!-- Add Project Button as the Last Item -->
                  <v-flex xs12 sm6 md4 lg3 class="pa-2">
                    <v-card
                      class="add-project-card rounded-lg pa-4 d-flex flex-column align-center justify-center elevation-0"
                      ripple
                      @click="openAddProject(item)"
                    >
                      <v-icon color="#005f32" size="32" class="mb-1">mdi-plus-circle-outline</v-icon>
                      <span class="kumbhBold" style="color: #005f32; font-size: 14px;">Add Project</span>
                      <span class="text-caption grey--text text-center">under this division</span>
                    </v-card>
                  </v-flex>
                </v-layout>
              </div>
            </div>
          </v-expand-transition>
        </v-card>
      </div>

      <!-- Empty State -->
      <v-card v-else class="elevation-1 rounded-xl pa-8 text-center bg-white">
        <v-icon size="64" color="grey lighten-1">mdi-folder-alert-outline</v-icon>
        <h3 class="kumbhBold grey--text text--darken-2 mt-3">No Divisions Found</h3>
        <p class="grey--text">No divisions matching your search or no divisions created yet.</p>
        <v-btn
          color="#005f32"
          dark
          class="rounded-xl px-5 text-none mt-2"
          @click="dialog = true"
        >
          <v-icon left>mdi-plus</v-icon>
          Add Your First Division
        </v-btn>
      </v-card>
    </v-container>

    <!-- Dialog: Add Division -->
    <v-dialog v-model="dialog" persistent max-width="500px">
      <v-card class="rounded-lg overflow-hidden">
        <v-form v-model="addcat" ref="addDivisionForm" @submit.prevent="adddivision">
          <div class="pa-4 primary-header" style="background: #005f32 !important;">
            <span class="kumbhBold white--text" style="font-size: 18px;">
              Add Division
            </span>
          </div>

          <v-card-text class="pt-6 px-6">
            <v-autocomplete
              outlined
              dense
              color="#005f32"
              v-model="departmentId"
              :items="departmentList"
              item-text="deptName"
              item-value="_id"
              :rules="[rules.required]"
              clearable
              class="mb-3"
              label="Department *"
            ></v-autocomplete>

            <v-text-field
              v-model="divisionName"
              label="Division Name *"
              :rules="[rules.required]"
              required
              outlined
              dense
            ></v-text-field>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text color="grey darken-1" class="text-none" @click="dialogclose">
              Cancel
            </v-btn>
            <v-btn
              color="#005f32"
              dark
              class="rounded-lg text-none px-5"
              :disabled="!addcat"
              @click="adddivision"
            >
              Save Division
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Dialog: Edit Division -->
    <v-dialog persistent v-model="editdialog" max-width="500px">
      <v-card class="rounded-lg overflow-hidden">
        <v-form v-model="editFormValid" ref="editDivisionForm" @submit.prevent="editDivisionSubmit">
          <div class="pa-4 primary-header" style="background: #005f32 !important;">
            <span class="kumbhBold white--text" style="font-size: 18px;">
              Edit Division
            </span>
          </div>

          <v-card-text class="pt-6 px-6">
            <v-autocomplete
              outlined
              dense
              color="#005f32"
              v-model="editingitem.departmentId"
              :items="departmentList"
              item-text="deptName"
              item-value="_id"
              :rules="[rules.required]"
              clearable
              class="mb-3"
              label="Department *"
            ></v-autocomplete>

            <v-text-field
              v-model="editingitem.divisiontName"
              label="Division Name *"
              :rules="[rules.required]"
              required
              outlined
              dense
            ></v-text-field>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text color="grey darken-1" class="text-none" @click="editdialog = false">
              Cancel
            </v-btn>
            <v-btn
              color="#005f32"
              dark
              class="rounded-lg text-none px-5"
              :disabled="!editFormValid"
              @click="editDivisionSubmit"
            >
              Update Division
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Dialog: Add Project under specific division -->
    <v-dialog v-model="addProjectDialog" persistent max-width="550px">
      <v-card class="rounded-lg overflow-hidden">
        <v-form v-model="addProjectValid" ref="addProjectForm" @submit.prevent="submitAddProject">
          <div class="pa-4 primary-header" style="background: #005f32 !important;">
            <span class="kumbhBold white--text" style="font-size: 18px;">
              Add Project
            </span>
            <div class="text-caption white--text opacity-80 mt-1" v-if="selectedDivisionForProject">
              Division: <strong>{{ selectedDivisionForProject.divisiontName }}</strong>
            </div>
          </div>

          <v-card-text class="pt-6 px-6">
            <v-text-field
              v-model="newProject.projectName"
              label="Project Name *"
              :rules="[rules.required]"
              required
              outlined
              dense
              class="mb-2"
            ></v-text-field>

            <v-autocomplete
              v-model="newProject.department"
              :items="departmentList"
              item-text="deptName"
              item-value="_id"
              :rules="[rules.required]"
              required
              outlined
              dense
              label="Department *"
              class="mb-2"
            ></v-autocomplete>

            <v-text-field
              v-model="newProject.location"
              label="Project Location"
              outlined
              dense
              class="mb-2"
            ></v-text-field>

            <v-textarea
              v-model="newProject.description"
              label="Project Description"
              rows="3"
              outlined
              dense
            ></v-textarea>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text color="grey darken-1" class="text-none" @click="closeAddProject">
              Cancel
            </v-btn>
            <v-btn
              color="#005f32"
              dark
              class="rounded-lg text-none px-5"
              :disabled="!addProjectValid"
              @click="submitAddProject"
            >
              Add Project
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Dialog: Edit Project -->
    <v-dialog v-model="editProjectDialog" persistent max-width="550px">
      <v-card class="rounded-lg overflow-hidden">
        <v-form v-model="editProjectValid" ref="editProjectForm" @submit.prevent="submitEditProject">
          <div class="pa-4 primary-header" style="background: #005f32 !important;">
            <span class="kumbhBold white--text" style="font-size: 18px;">
              Edit Project
            </span>
          </div>

          <v-card-text class="pt-6 px-6">
            <v-text-field
              v-model="editingProject.projectName"
              label="Project Name *"
              :rules="[rules.required]"
              required
              outlined
              dense
              class="mb-2"
            ></v-text-field>

            <v-autocomplete
              v-model="editingProject.department"
              :items="departmentList"
              item-text="deptName"
              item-value="_id"
              :rules="[rules.required]"
              required
              outlined
              dense
              label="Department *"
              class="mb-2"
            ></v-autocomplete>

            <v-autocomplete
              v-model="editingProject.divisiontName"
              :items="divisionlist"
              item-text="divisiontName"
              item-value="_id"
              :rules="[rules.required]"
              required
              outlined
              dense
              label="Division *"
              class="mb-2"
            ></v-autocomplete>

            <v-text-field
              v-model="editingProject.location"
              label="Project Location"
              outlined
              dense
              class="mb-2"
            ></v-text-field>

            <v-textarea
              v-model="editingProject.description"
              label="Project Description"
              rows="3"
              outlined
              dense
            ></v-textarea>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text color="grey darken-1" class="text-none" @click="editProjectDialog = false">
              Cancel
            </v-btn>
            <v-btn
              color="#005f32"
              dark
              class="rounded-lg text-none px-5"
              :disabled="!editProjectValid"
              @click="submitEditProject"
            >
              Update Project
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Dialog: Delete Division Confirmation -->
    <v-dialog persistent v-model="deleteDivisionDialog" max-width="500px">
      <v-card class="rounded-lg overflow-hidden">
        <div class="pa-4 error-header" style="background: #005f32 !important;">
          <span class="kumbhBold white--text" style="font-size: 18px;">
            Delete Division Confirmation
          </span>
        </div>
        <v-card-text class="pt-6 px-6 text-body-1" v-if="deletingDivisionItem">
          Are you sure you want to delete the division
          <strong>"{{ deletingDivisionItem.divisiontName }}"</strong>?
          <div class="text-caption grey--text mt-2" v-if="getProjectsForDivision(deletingDivisionItem._id).length > 0">
            Note: This division currently has {{ getProjectsForDivision(deletingDivisionItem._id).length }} projects associated with it.
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" class="text-none" @click="deleteDivisionDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" class="rounded-lg text-none px-5" @click="executeDeleteDivision">
            Delete Division
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Delete Project Confirmation -->
    <v-dialog persistent v-model="deleteProjectDialog" max-width="500px">
      <v-card class="rounded-lg overflow-hidden">
        <div class="pa-4 error-header" style="background: #005f32 !important;">
          <span class="kumbhBold white--text" style="font-size: 18px;">
            Delete Project Confirmation
          </span>
        </div>
        <v-card-text class="pt-6 px-6 text-body-1" v-if="deletingProjectItem">
          Are you sure you want to delete project
          <strong>"{{ deletingProjectItem.projectName }}"</strong>?
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" class="text-none" @click="deleteProjectDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" class="rounded-lg text-none px-5" @click="executeDeleteProject">
            Delete Project
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "AddDivision",
  data() {
    return {
      showsnackbar: false,
      snackbarColor: "black",
      ServerError: false,
      msg: null,
      appLoading: false,

      // Search and Filter
      searchKeyword: "",

      // Collapsed / Minimized Divisions
      collapsedDivisions: [],
      initialCollapseDone: false,

      // Division state
      dialog: false,
      editdialog: false,
      addcat: false,
      editFormValid: false,
      divisionName: null,
      departmentId: null,
      divisionlist: [],
      editingitem: {},
      deleteDivisionDialog: false,
      deletingDivisionItem: null,

      // Project state
      projectList: [],
      addProjectDialog: false,
      editProjectDialog: false,
      deleteProjectDialog: false,
      addProjectValid: false,
      editProjectValid: false,
      selectedDivisionForProject: null,
      deletingProjectItem: null,
      newProject: {
        projectName: "",
        department: null,
        location: "",
        description: "",
      },
      editingProject: {
        id: null,
        projectName: "",
        department: null,
        divisiontName: null,
        location: "",
        description: "",
      },

      // Shared
      departmentList: [],
      rules: {
        required: (value) => !!value || "This field is required.",
      },
    };
  },

  computed: {
    areAllCollapsed() {
      if (!this.divisionlist.length) return false;
      return this.divisionlist.every((div) => this.collapsedDivisions.includes(div._id));
    },

    filteredDivisions() {
      if (!this.searchKeyword || !this.searchKeyword.trim()) {
        return this.divisionlist;
      }
      const kw = this.searchKeyword.trim().toLowerCase();
      return this.divisionlist.filter((div) => {
        const divNameMatch =
          div.divisiontName && div.divisiontName.toLowerCase().includes(kw);
        const deptNameMatch =
          div.departmentId &&
          div.departmentId.deptName &&
          div.departmentId.deptName.toLowerCase().includes(kw);

        // Also check if any project under this division matches
        const projects = this.getProjectsForDivision(div._id);
        const projectMatch = projects.some(
          (p) =>
            (p.projectName && p.projectName.toLowerCase().includes(kw)) ||
            (p.location && p.location.toLowerCase().includes(kw))
        );

        return divNameMatch || deptNameMatch || projectMatch;
      });
    },
  },

  watch: {
    searchKeyword() {
      this.getlist();
    },
  },

  mounted() {
    this.getDepartments();
    this.getlist();
    this.getAllProjects();
  },

  methods: {
    // Minimize / Expand logic
    isCollapsed(divisionId) {
      return this.collapsedDivisions.includes(divisionId);
    },

    toggleDivision(divisionId) {
      const idx = this.collapsedDivisions.indexOf(divisionId);
      if (idx > -1) {
        this.collapsedDivisions.splice(idx, 1);
      } else {
        this.collapsedDivisions.push(divisionId);
      }
    },

    toggleAllDivisions() {
      if (this.areAllCollapsed) {
        this.collapsedDivisions = [];
      } else {
        this.collapsedDivisions = this.divisionlist.map((d) => d._id);
      }
    },

    // Show toast notification
    notify(msg, color = "black") {
      this.msg = msg;
      this.snackbarColor = color;
      this.showsnackbar = true;
    },

    // Pull the server's error message out of a failed request
    getErrorMessage(err) {
      const data = err && err.response && err.response.data;
      return (data && (data.msg || data.message)) || "Something went wrong!";
    },

    // Get Projects for specific division
    getProjectsForDivision(divisionId) {
      if (!this.projectList || !this.projectList.length || !divisionId) return [];
      return this.projectList.filter((p) => {
        if (!p.division) return false;
        const pDivId = typeof p.division === "object" ? p.division._id : p.division;
        return String(pDivId) === String(divisionId);
      });
    },

    // Fetch Departments
    getDepartments() {
      this.appLoading = true;
      axios({
        url: "/department/getalllist",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.departmentList = response.data.data || [];
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },

    // Fetch Divisions
    getlist() {
      this.appLoading = true;
      axios({
        url: "/division/list",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: { searchKeyword: this.searchKeyword },
      })
        .then((response) => {
          this.appLoading = false;
          this.divisionlist = response.data.data || [];
          // Keep all divisions minimized by default on first load
          if (!this.initialCollapseDone) {
            this.collapsedDivisions = this.divisionlist.map((d) => d._id);
            this.initialCollapseDone = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },

    // Fetch all projects
    getAllProjects() {
      this.appLoading = true;
      axios({
        url: "/projects/getalllist",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.projectList = response.data.data || [];
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },

    // Add Division
    dialogclose() {
      this.divisionName = null;
      this.departmentId = null;
      this.dialog = false;
      if (this.$refs.addDivisionForm) {
        this.$refs.addDivisionForm.resetValidation();
      }
    },

    adddivision() {
      if (!this.departmentId || !this.divisionName) {
        this.notify("Please fill all required fields", "error");
        return;
      }
      this.appLoading = true;
      const data = {
        departmentId: this.departmentId,
        divisionName: this.divisionName,
      };
      axios({
        url: "/division/add",
        method: "POST",
        data: data,
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.notify("Division added successfully", "#005f32");
            this.dialogclose();
            this.getlist();
          } else {
            this.notify(response.data.msg || "Failed to add division", "error");
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.notify(this.getErrorMessage(err), "error");
          console.error(err);
        });
    },

    // Edit Division
    editDivision(item) {
      this.editingitem = {
        _id: item._id,
        divisiontName: item.divisiontName,
        departmentId: item.departmentId ? (typeof item.departmentId === "object" ? item.departmentId._id : item.departmentId) : null,
      };
      this.editdialog = true;
    },

    editDivisionSubmit() {
      if (!this.editingitem.divisiontName || !this.editingitem.departmentId) {
        this.notify("Please fill all required fields", "error");
        return;
      }
      this.appLoading = true;
      const data = {
        id: this.editingitem._id,
        departmentId: this.editingitem.departmentId,
        divisionName: this.editingitem.divisiontName,
      };
      axios({
        url: "/division/edit",
        method: "POST",
        data: data,
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.notify("Division updated successfully", "#005f32");
            this.editdialog = false;
            this.getlist();
            this.getAllProjects();
          } else {
            this.notify(response.data.msg || "Failed to update division", "error");
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.notify(this.getErrorMessage(err), "error");
          console.error(err);
        });
    },

    // Delete Division
    confirmDeleteDivision(item) {
      this.deletingDivisionItem = item;
      this.deleteDivisionDialog = true;
    },

    executeDeleteDivision() {
      if (!this.deletingDivisionItem) return;
      this.appLoading = true;
      const data = { id: this.deletingDivisionItem._id };
      axios({
        url: "/division/remove",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: data,
      })
        .then((response) => {
          this.appLoading = false;
          this.deleteDivisionDialog = false;
          if (response.data.status) {
            this.notify("Division deleted successfully", "#005f32");
            this.getlist();
            this.getAllProjects();
          } else {
            this.notify(response.data.msg || "Failed to delete division", "error");
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.deleteDivisionDialog = false;
          this.notify(this.getErrorMessage(err), "error");
          console.error(err);
        });
    },

    // Add Project Modal
    openAddProject(divisionItem) {
      this.selectedDivisionForProject = divisionItem;
      const deptId = divisionItem.departmentId
        ? typeof divisionItem.departmentId === "object"
          ? divisionItem.departmentId._id
          : divisionItem.departmentId
        : null;

      this.newProject = {
        projectName: "",
        department: deptId,
        location: "",
        description: "",
      };
      this.addProjectDialog = true;
    },

    closeAddProject() {
      this.addProjectDialog = false;
      this.selectedDivisionForProject = null;
      this.newProject = {
        projectName: "",
        department: null,
        location: "",
        description: "",
      };
      if (this.$refs.addProjectForm) {
        this.$refs.addProjectForm.resetValidation();
      }
    },

    submitAddProject() {
      if (!this.newProject.projectName || !this.newProject.department || !this.selectedDivisionForProject) {
        this.notify("Please fill all required fields", "error");
        return;
      }
      this.appLoading = true;
      const data = {
        projectName: this.newProject.projectName,
        department: this.newProject.department,
        divisiontName: this.selectedDivisionForProject._id,
        location: this.newProject.location,
        description: this.newProject.description,
      };

      axios({
        url: "/projects/add",
        method: "POST",
        data: data,
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.notify("Project added successfully", "#005f32");
            this.closeAddProject();
            this.getAllProjects();
          } else {
            this.notify(response.data.msg || "Failed to add project", "error");
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.notify(this.getErrorMessage(err), "error");
          console.error(err);
        });
    },

    // Edit Project Modal
    openEditProject(project) {
      let deptId = null;
      if (project.department) {
        if (Array.isArray(project.department) && project.department.length > 0) {
          deptId = project.department[0]._id || project.department[0];
        } else if (typeof project.department === "object") {
          deptId = project.department._id;
        } else {
          deptId = project.department;
        }
      }

      let divId = null;
      if (project.division) {
        divId = typeof project.division === "object" ? project.division._id : project.division;
      }

      this.editingProject = {
        id: project._id,
        projectName: project.projectName || "",
        department: deptId,
        divisiontName: divId,
        location: project.location || "",
        description: project.description || "",
      };
      this.editProjectDialog = true;
    },

    submitEditProject() {
      if (!this.editingProject.projectName || !this.editingProject.department || !this.editingProject.divisiontName) {
        this.notify("Please fill all required fields", "error");
        return;
      }
      this.appLoading = true;
      const data = {
        id: this.editingProject.id,
        projectName: this.editingProject.projectName,
        department: this.editingProject.department,
        divisiontName: this.editingProject.divisiontName,
        location: this.editingProject.location,
        description: this.editingProject.description,
      };

      axios({
        url: "/projects/edit",
        method: "POST",
        data: data,
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.notify("Project updated successfully", "#005f32");
            this.editProjectDialog = false;
            this.getAllProjects();
          } else {
            this.notify(response.data.msg || "Failed to update project", "error");
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.notify(this.getErrorMessage(err), "error");
          console.error(err);
        });
    },

    // Delete Project Modal
    confirmDeleteProject(project) {
      this.deletingProjectItem = project;
      this.deleteProjectDialog = true;
    },

    executeDeleteProject() {
      if (!this.deletingProjectItem) return;
      this.appLoading = true;
      const data = { id: this.deletingProjectItem._id };
      axios({
        url: "/projects/remove",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: data,
      })
        .then((response) => {
          this.appLoading = false;
          this.deleteProjectDialog = false;
          if (response.data.status) {
            this.notify("Project deleted successfully", "#005f32");
            this.getAllProjects();
          } else {
            this.notify(response.data.msg || "Failed to delete project", "error");
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.deleteProjectDialog = false;
          this.notify(this.getErrorMessage(err), "error");
          console.error(err);
        });
    },
  },
};
</script>

<style scoped>
.division-management-page {
  background-color: #f4f6f8;
  min-height: 100vh;
}

.division-card {
  border-left: 6px solid #005f32 !important;
  background-color: #ffffff !important;
  transition: all 0.2s ease-in-out;
  overflow: hidden;
}

.division-card:hover {
  box-shadow: 0 6px 18px rgba(0, 95, 50, 0.08) !important;
}

.division-header {
  background-color: #ffffff;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
}

.division-header:hover {
  background-color: #f7faf8;
}

.division-name {
  font-size: 19px;
  color: #1a2e22;
  letter-spacing: -0.2px;
}

.collapse-chevron {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.chevron-rotated {
  transform: rotate(180deg);
}

.projects-section {
  background-color: #fafbfb;
  border-top: 1px solid #edf2f7;
}

.project-card {
  background-color: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.project-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06) !important;
  border-color: #cbd5e1 !important;
}

.project-title {
  font-size: 14px;
  color: #1e293b;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.project-desc {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.3;
}

.border-top-subtle {
  border-top: 1px dashed #e2e8f0;
}

.add-project-card {
  border: 2px dashed #005f32 !important;
  background-color: #f0f7f3 !important;
  min-height: 120px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-project-card:hover {
  background-color: #e2f0e8 !important;
  border-color: #004d28 !important;
  transform: translateY(-2px);
}

.action-btn:hover {
  background-color: rgba(0, 95, 50, 0.08);
}

.opacity-80 {
  opacity: 0.85;
}

.search-field {
  max-width: 280px;
}

.snackbar-msg {
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-word;
}

.division-info {
  min-width: 0;
}

.division-name {
  word-break: break-word;
}

.division-actions {
  flex-shrink: 0;
}

@media (max-width: 599px) {
  .toolbar-actions > * {
    margin-right: 0 !important;
    margin-bottom: 8px;
    width: 100%;
    max-width: 100%;
  }

  .division-header {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  .division-name {
    font-size: 16px;
  }

  .projects-section {
    padding: 8px !important;
  }
}
</style>
